import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import axios from 'api/axios'
import CustomDateTimeComponent from 'common/components/CustomDateTimeComponent'
import CustomTable from 'common/components/CustomTable'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  OBJETIVOS_OPTIMIZACION,
  crearCriterioHibrido,
  esCriterioHibrido,
} from '../../data/objetivos'
import { PlanificacionesHelper } from '../../helpers/PlanificacionesHelper'
import { usePlanificaciones } from '../../providers/PlanificacionesProvider'
import ModalRangoPedidos from '../shared/ModalRangoPedidos'
import ModalSeleccionPedidos from '../shared/ModalSeleccionPedidos'

export default function FormView() {
  // Contexto: obtener estado y funciones de navegación
  const {
    selectedPlanificacion,
    formMode,
    goToDashboard,
    triggerRefresh,
    updatePlanificacionStatus,
  } = usePlanificaciones()

  // Determinar modo
  const isEditMode = formMode === 'edit'
  const isCreateMode = formMode === 'create'

  // Estado para modales
  const [modalPedidosOpen, setModalPedidosOpen] = useState(false)
  const [modalRangoOpen, setModalRangoOpen] = useState(false)

  // Estado del formulario
  const [formData, setFormData] = useState({
    fecha_inicio: null,
    descripcion: '',
    criterio_optimizacion: 'total_retraso',
    segundos_maximo: 20,
    prevision_produccion: false,
    enforce_due_dates: false,
    id_planta: null,
    pedidos: [],
    status: 'A',
    mensaje_error: '',
  })

  // Plantas disponibles
  const [plantas, setPlantas] = useState([])

  useEffect(() => {
    axios
      .get('/optprod/adm/plantas/')
      .then((res) => {
        setPlantas(res.data)
        if (res.data.length > 0) {
          setFormData((prev) => ({ ...prev, id_planta: prev.id_planta ?? res.data[0].id }))
        }
      })
      .catch(() => {})
  }, [])

  // Estado para guardar los datos originales (para detectar cambios)
  const [formDataOriginal, setFormDataOriginal] = useState(null)

  // Función helper para mostrar notificaciones
  const mostrarNotificacion = useCallback((message, severity = 'info') => {
    enqueueSnackbar(message, { variant: severity })
  }, [])

  // Función para detectar si hay cambios no guardados
  const hayaCambiosNoGuardados = useCallback(() => {
    if (!formDataOriginal) return false

    // Comparar campos básicos
    if (formData.descripcion !== formDataOriginal.descripcion) return true
    if (
      JSON.stringify(formData.criterio_optimizacion) !==
      JSON.stringify(formDataOriginal.criterio_optimizacion)
    )
      return true
    if (formData.segundos_maximo !== formDataOriginal.segundos_maximo) return true
    if (formData.prevision_produccion !== formDataOriginal.prevision_produccion) return true
    if (formData.enforce_due_dates !== formDataOriginal.enforce_due_dates) return true

    // Comparar fechas (usar isSame de dayjs)
    const fechaActual = formData.fecha_inicio
    const fechaOriginal = formDataOriginal.fecha_inicio
    if (fechaActual && fechaOriginal && !fechaActual.isSame(fechaOriginal)) return true
    if (!fechaActual && fechaOriginal) return true
    if (fechaActual && !fechaOriginal) return true

    // Comparar pedidos (solo IDs)
    const pedidosActuales = formData.pedidos.map((p) => p.id).sort()
    const pedidosOriginales = formDataOriginal.pedidos.map((p) => p.id).sort()
    if (pedidosActuales.length !== pedidosOriginales.length) return true
    if (pedidosActuales.some((id, i) => id !== pedidosOriginales[i])) return true

    return false
  }, [formData, formDataOriginal])

  // Estado para Dialog (confirmaciones)
  const [dialog, setDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  })

  // Funciones para manejar el Dialog
  const mostrarConfirmacion = (title, message, onConfirm) => {
    setDialog({ open: true, title, message, onConfirm })
  }

  const cerrarDialog = () => {
    setDialog({ open: false, title: '', message: '', onConfirm: null })
  }

  const confirmarDialog = () => {
    const callback = dialog.onConfirm // Guardar referencia antes de cerrar
    cerrarDialog() // Cerrar primero
    callback && callback() // Ejecutar callback después
  }

  // Determinar si el formulario es editable
  const isEditable = formData.status !== 'O'

  // Cargar datos si es modo edición
  useEffect(() => {
    const cargarDatos = async () => {
      if (!isEditMode || !selectedPlanificacion) return

      try {
        const planificacion = await PlanificacionesHelper.getPlanificacionById(
          selectedPlanificacion.id,
        )
        const params = new URLSearchParams()
        params.append('planificacion_seleccionada', selectedPlanificacion.id)
        const response = await axios.get(`/optprod/adm/pedidos/?${params.toString()}`)
        const pedidosAsignados = response.data

        const pedidosDeEstaPlanificacion = pedidosAsignados.filter(
          (pedido) => pedido.id_planificacion_pedido,
        )

        setFormData({
          fecha_inicio: planificacion.fecha_inicio
            ? dayjs(planificacion.fecha_inicio.replace(/[Z]$/, '').replace(/[+-]\d{2}:\d{2}$/, ''))
            : null,
          descripcion: planificacion.descripcion || '',
          criterio_optimizacion: planificacion.criterio_optimizacion || 'total_retraso',
          segundos_maximo: planificacion.segundos_maximo || 20,
          prevision_produccion: planificacion.prevision_produccion || false,
          enforce_due_dates: planificacion.enforce_due_dates || false,
          id_planta: planificacion.id_planta || null,
          pedidos: pedidosDeEstaPlanificacion,
          status: planificacion.status || 'A',
          mensaje_error: planificacion.mensaje_error || '',
        })

        updatePlanificacionStatus(planificacion.status)

        // Guardar una copia de los datos originales para detectar cambios
        setFormDataOriginal({
          fecha_inicio: planificacion.fecha_inicio
            ? dayjs(planificacion.fecha_inicio.replace(/[Z]$/, '').replace(/[+-]\d{2}:\d{2}$/, ''))
            : null,
          descripcion: planificacion.descripcion || '',
          criterio_optimizacion: planificacion.criterio_optimizacion || 'total_retraso',
          segundos_maximo: planificacion.segundos_maximo || 20,
          prevision_produccion: planificacion.prevision_produccion || false,
          enforce_due_dates: planificacion.enforce_due_dates || false,
          id_planta: planificacion.id_planta || null,
          pedidos: pedidosDeEstaPlanificacion,
          status: planificacion.status || 'A',
          mensaje_error: planificacion.mensaje_error || '',
        })
      } catch (error) {
        mostrarNotificacion('Error al cargar los datos de la planificación', 'error')
      }
    }

    cargarDatos()
  }, [isEditMode, selectedPlanificacion, mostrarNotificacion])

  // Polling cada 15s mientras la planificación está en estado 'O' (en optimizador)
  const pollingRef = useRef(null)
  useEffect(() => {
    if (formData.status !== 'O' || !selectedPlanificacion?.id) {
      clearInterval(pollingRef.current)
      return
    }

    pollingRef.current = setInterval(async () => {
      try {
        const planificacion = await PlanificacionesHelper.getPlanificacionById(
          selectedPlanificacion.id,
        )
        if (planificacion.status !== 'O') {
          setFormData((prev) => ({
            ...prev,
            status: planificacion.status,
            mensaje_error: planificacion.mensaje_error || '',
          }))
          updatePlanificacionStatus(planificacion.status)
          triggerRefresh()

          if (planificacion.status === 'Z') {
            mostrarNotificacion('La optimización ha finalizado correctamente', 'success')
          } else if (planificacion.status === 'X') {
            mostrarNotificacion(
              `La optimización ha finalizado con errores: ${planificacion.mensaje_error || ''}`,
              'error',
            )
          }
        }
      } catch {
        // Ignorar errores de polling para no saturar al usuario
      }
    }, 15000)

    return () => clearInterval(pollingRef.current)
  }, [
    formData.status,
    selectedPlanificacion?.id,
    mostrarNotificacion,
    updatePlanificacionStatus,
    triggerRefresh,
  ])

  const handleGuardar = async () => {
    // Validación
    if (!formData.fecha_inicio || !formData.descripcion) {
      mostrarNotificacion('Por favor, completa todos los campos obligatorios', 'warning')
      return
    }

    // Formatear datos
    const dataToSend = {
      fecha_inicio: formData.fecha_inicio.format('YYYY-MM-DDTHH:mm:ss'),
      descripcion: formData.descripcion,
      criterio_optimizacion: formData.criterio_optimizacion,
      segundos_maximo: formData.segundos_maximo,
      prevision_produccion: formData.prevision_produccion,
      enforce_due_dates: formData.enforce_due_dates,
      id_planta: formData.id_planta,
      status: formData.status,
    }

    try {
      let planificacionId

      if (isEditMode) {
        await PlanificacionesHelper.updatePlanificacion(selectedPlanificacion.id, dataToSend)
        planificacionId = selectedPlanificacion.id
      } else {
        const nuevaPlanificacion = await PlanificacionesHelper.createPlanificacion(dataToSend)
        planificacionId = nuevaPlanificacion.id
      }

      if (!isEditMode) {
        goToDashboard()
      }

      // Sincronizar pedidos
      if (formData.pedidos.length > 0) {
        const pedidosIds = formData.pedidos.map((pedido) => pedido.id)
        await PlanificacionesHelper.sincronizarPedidos(planificacionId, pedidosIds)
      }

      mostrarNotificacion(
        isEditMode
          ? `Planificación ID: ${selectedPlanificacion?.id} actualizada correctamente`
          : 'Planificación creada correctamente',
        'success',
      )

      triggerRefresh() // Refrescar datos en dashboard
      // setTimeout(() => goToDashboard(), 1200) // Volver al dashboard

      // Actualizar datos originales después de guardar exitosamente
      setFormDataOriginal({ ...formData })
    } catch (error) {
      mostrarNotificacion('Error al guardar: ' + error.message, 'error')
    }
  }

  const handleCancelar = () => {
    mostrarConfirmacion(
      'Cancelar planificación',
      '¿Desea cancelar? Se perderán los cambios no guardados.',
      () => goToDashboard(),
    )
  }

  // Función que gestiona el envío al optimizador (con o sin guardar cambios)
  const enviarAlOptimizador = () => {
    if (hayaCambiosNoGuardados()) {
      mostrarConfirmacion(
        '⚠️ Cambios no guardados',
        'Hay cambios sin guardar. Si envías al optimizador ahora, se utilizarán los datos del último guardado. ¿Deseas guardar los cambios y enviar al optimizador?',
        async () => {
          try {
            // Primero guardar los cambios
            const dataToSend = {
              fecha_inicio: formData.fecha_inicio.format('YYYY-MM-DDTHH:mm:ss'),
              descripcion: formData.descripcion,
              criterio_optimizacion: formData.criterio_optimizacion,
              segundos_maximo: formData.segundos_maximo,
              prevision_produccion: formData.prevision_produccion,
              enforce_due_dates: formData.enforce_due_dates,
              id_planta: formData.id_planta,
              status: formData.status,
            }

            await PlanificacionesHelper.updatePlanificacion(selectedPlanificacion.id, dataToSend)

            // Sincronizar pedidos
            if (formData.pedidos.length > 0) {
              const pedidosIds = formData.pedidos.map((pedido) => pedido.id)
              await PlanificacionesHelper.sincronizarPedidos(selectedPlanificacion.id, pedidosIds)
            }

            mostrarNotificacion('Cambios guardados correctamente', 'success')

            // Actualizar datos originales después de guardar
            setFormDataOriginal({ ...formData })

            // Ahora enviar al optimizador
            await axios.post(`/planificaciones/optimizar/${selectedPlanificacion.id}/`)
            const response = await axios.get(`/planificaciones/${selectedPlanificacion.id}/`)
            setFormData((prev) => ({ ...prev, status: response.data.status }))
            updatePlanificacionStatus(response.data.status)
            mostrarNotificacion(
              `Planificación ID: ${selectedPlanificacion?.id} enviada al optimizador correctamente`,
              'success',
            )
            triggerRefresh()
          } catch (error) {
            console.error('Error al guardar y enviar:', error)
            if (error.response?.status === 400) {
              mostrarNotificacion(
                error.response?.data?.detail ||
                  'La planificación no tiene líneas de pedido asignadas.',
                'warning',
              )
              return
            }
            if (error.response?.status === 409) {
              mostrarNotificacion(
                error.response?.data?.detail ||
                  'Ya hay una optimización en ejecución. Espere a que termine.',
                'warning',
              )
            } else {
              mostrarNotificacion('Error al guardar y enviar: ' + error.message, 'error')
            }
          }
        },
      )
    } else {
      // No hay cambios, enviar directamente
      mostrarConfirmacion(
        'Enviar al optimizador',
        '¿Desea enviar esta planificación al optimizador? Una vez enviada, no podrás editarla hasta que termine el proceso.',
        async () => {
          try {
            await axios.post(`/planificaciones/optimizar/${selectedPlanificacion.id}/`)
            const response = await axios.get(`/planificaciones/${selectedPlanificacion.id}/`)
            setFormData((prev) => ({ ...prev, status: response.data.status }))
            updatePlanificacionStatus(response.data.status)
            mostrarNotificacion(
              `Planificación ID: ${selectedPlanificacion?.id} enviada al optimizador correctamente`,
              'success',
            )
            triggerRefresh()
          } catch (error) {
            console.error('Error al enviar al optimizador:', error)
            if (error.response?.status === 400) {
              mostrarNotificacion(
                error.response?.data?.detail ||
                  'La planificación no tiene líneas de pedido asignadas.',
                'warning',
              )
              return
            }
            if (error.response?.status === 409) {
              mostrarNotificacion(
                error.response?.data?.detail ||
                  'Ya hay una optimización en ejecución. Espere a que termine.',
                'warning',
              )
            } else {
              mostrarNotificacion('Error al enviar al optimizador: ' + error.message, 'error')
            }
          }
        },
      )
    }
  }

  // Handler principal: primero verifica incidencias, luego delega a enviarAlOptimizador
  const handleEnviarOptimizador = () => {
    // Validar que haya líneas de pedido asignadas
    if (!formData.pedidos || formData.pedidos.length === 0) {
      mostrarNotificacion(
        'No se puede enviar al optimizador: la planificación no tiene líneas de pedidos asignadas.',
        'warning',
      )
      return
    }

    // Validación existente de incidencias
    const pedidosConIncidencia = formData.pedidos.filter((p) => p.tiene_incidencia)

    if (pedidosConIncidencia.length > 0) {
      mostrarConfirmacion(
        '⚠️ Pedidos con incidencias',
        `Hay ${pedidosConIncidencia.length} pedido(s) con incidencias que pueden causar errores en la optimización. ¿Desea continuar de todas formas?`,
        () => enviarAlOptimizador(),
      )
      return
    }

    enviarAlOptimizador()
  }

  return (
    <Box>
      {/* Estado de Planificación - Solo visible en modo edición */}
      {isEditMode && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Estado de la Planificación
              </Typography>
              <Chip
                label={
                  formData.status === 'A'
                    ? 'Pendiente de enviar'
                    : formData.status === 'O'
                      ? 'En optimizador'
                      : formData.status === 'X'
                        ? 'Con errores'
                        : formData.status === 'Z'
                          ? 'Optimización finalizada'
                          : 'Desconocido'
                }
                color={
                  formData.status === 'Z'
                    ? 'success'
                    : formData.status === 'O'
                      ? 'info'
                      : formData.status === 'X'
                        ? 'error'
                        : 'default'
                }
                size="medium"
                sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
              />
            </Box>

            {formData.status === 'O' && (
              <Alert severity="warning">
                <strong>Planificación en proceso:</strong> La planificación está siendo procesada
                por el optimizador. No se puede editar hasta que termine.
              </Alert>
            )}

            {formData.status === 'Z' && (
              <Alert severity="success">
                La optimización se completó exitosamente. Puede revisar los resultados en la pestaña{' '}
                <strong>Diagrama de Gantt</strong> o realizar ajustes y volver a optimizar.
              </Alert>
            )}

            {formData.status === 'X' && (
              <Alert severity="error">
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Error en la optimización.</strong> Puede editar la planificación y
                    volver a intentar.
                  </Typography>

                  {formData.mensaje_error && (
                    <Accordion
                      sx={{
                        mt: 1.5,
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        bgcolor: 'transparent',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 0,
                          minHeight: 40,
                          '& .MuiAccordionSummary-content': { my: 0.5 },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Ver detalles técnicos
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            whiteSpace: 'pre-wrap',
                            bgcolor: 'rgba(0,0,0,0.05)',
                            p: 1.5,
                            borderRadius: 1,
                          }}
                        >
                          {formData.mensaje_error}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              </Alert>
            )}
          </Box>
        </Paper>
      )}

      {!isEditable && formData.status !== 'Z' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Información: </strong>El diagrama de Gantt estará disponible cuando la
          optimización esté finalizada.
        </Alert>
      )}

      {/* Formulario */}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Fecha de Inicio */}
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <CustomDateTimeComponent
              label="Fecha de Inicio"
              value={formData.fecha_inicio}
              onChange={(newValue) => setFormData({ ...formData, fecha_inicio: newValue })}
              required={true}
              disabled={!isEditable}
            />
          </LocalizationProvider>

          {/* Descripción */}
          <TextField
            fullWidth
            label="Descripción"
            multiline
            rows={3}
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            slotProps={{
              htmlInput: { maxLength: 400 },
            }}
            helperText={`${formData.descripcion.length}/400 caracteres`}
            required
            disabled={!isEditable}
          />

          {/* Criterio de optimización */}
          <FormControl fullWidth disabled={!isEditable}>
            <InputLabel>Criterio de Optimización</InputLabel>
            <Select
              value={
                esCriterioHibrido(formData.criterio_optimizacion)
                  ? 'hybrid'
                  : formData.criterio_optimizacion
              }
              label="Criterio de Optimización"
              onChange={(e) => {
                const valor = e.target.value
                if (valor === 'hybrid') {
                  setFormData({ ...formData, criterio_optimizacion: crearCriterioHibrido() })
                } else {
                  setFormData({ ...formData, criterio_optimizacion: valor })
                }
              }}
            >
              {OBJETIVOS_OPTIMIZACION.map((obj) => (
                <MenuItem key={obj.name} value={obj.name}>
                  {obj.label}
                </MenuItem>
              ))}
              <MenuItem value="hybrid">
                <em>Personalizado (Híbrido)</em>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Panel de pesos para función híbrida */}
          {esCriterioHibrido(formData.criterio_optimizacion) && (
            <Paper variant="outlined" sx={{ p: 3, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Configuración de Pesos
              </Typography>
              {OBJETIVOS_OPTIMIZACION.map((obj) => {
                const objetivo = formData.criterio_optimizacion.objectives.find(
                  (o) => o.name === obj.name,
                )
                const peso = objetivo?.weight ?? 0
                return (
                  <Box key={obj.name} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {obj.label}: <strong>{peso}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {obj.description}
                    </Typography>
                    <Slider
                      value={peso}
                      onChange={(_, newValue) => {
                        const nuevosObjetivos = formData.criterio_optimizacion.objectives.map(
                          (o) => (o.name === obj.name ? { ...o, weight: newValue } : o),
                        )
                        setFormData({
                          ...formData,
                          criterio_optimizacion: {
                            ...formData.criterio_optimizacion,
                            objectives: nuevosObjetivos,
                          },
                        })
                      }}
                      min={0}
                      max={1}
                      step={0.1}
                      marks={[
                        { value: 0, label: '0' },
                        { value: 0.5, label: '0.5' },
                        { value: 1, label: '1' },
                      ]}
                      valueLabelDisplay="auto"
                      disabled={!isEditable}
                      size="small"
                    />
                  </Box>
                )
              })}
            </Paper>
          )}

          {/* Segundos Máximo */}
          <Box sx={{ px: { xs: 0, sm: 4, md: 8, lg: 12 }, textAlign: 'center' }}>
            <Typography gutterBottom>Segundos Máximo: {formData.segundos_maximo}</Typography>
            <Slider
              value={formData.segundos_maximo}
              onChange={(e, newValue) => setFormData({ ...formData, segundos_maximo: newValue })}
              min={1}
              max={900}
              step={1}
              marks={[
                { value: 1, label: '1s' },
                { value: 20, label: '20s' },
                { value: 60, label: '1min' },
                { value: 120, label: '2min' },
                { value: 180, label: '3min' },
                { value: 300, label: '5min' },
                { value: 480, label: '8min' },
                { value: 600, label: '10min' },
                { value: 900, label: '15min' },
              ]}
              valueLabelDisplay="auto"
              disabled={!isEditable}
            />
          </Box>

          {/* Restricciones */}
          <FormControl fullWidth disabled={!isEditable}>
            <InputLabel>Restricciones</InputLabel>
            <Select
              multiple
              label="Restricciones"
              value={[
                ...(formData.prevision_produccion ? ['PREVISION_PRODUCCION'] : []),
                ...(formData.enforce_due_dates ? ['ENFORCE_DUE_DATES'] : []),
              ]}
              // onChange
              onChange={(e) => {
                const values = e.target.value
                setFormData({
                  ...formData,
                  prevision_produccion: values.includes('PREVISION_PRODUCCION'),
                  enforce_due_dates: values.includes('ENFORCE_DUE_DATES'),
                })
              }}
              // renderValue
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.includes('PREVISION_PRODUCCION') && (
                    <Chip label="Previsión de Producción" size="small" />
                  )}
                  {selected.includes('ENFORCE_DUE_DATES') && (
                    <Chip label="Fecha de Entrega" size="small" />
                  )}
                </Box>
              )}
            >
              <MenuItem value="PREVISION_PRODUCCION">
                <Checkbox checked={formData.prevision_produccion} />
                <Typography>Previsión de Producción</Typography>
              </MenuItem>
              <MenuItem value="ENFORCE_DUE_DATES">
                <Checkbox checked={formData.enforce_due_dates} />
                <Typography>Fecha de Entrega</Typography>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Planta */}
          <FormControl fullWidth disabled={!isEditable}>
            <InputLabel>Planta</InputLabel>
            <Select
              value={formData.id_planta ?? ''}
              label="Planta"
              onChange={(e) =>
                setFormData({ ...formData, id_planta: e.target.value || null })
              }
            >
              {plantas.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.descripcion || p.planta}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Botones de acción - Solo en modo CREAR */}
          {isCreateMode && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="outlined" onClick={handleCancelar}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={handleGuardar} disabled={!isEditable}>
                Guardar Planificación
              </Button>
            </Box>
          )}

          {/* Sección de Pedidos - Solo en modo EDITAR */}
          {isEditMode && (
            <Box
              sx={{ opacity: isEditable ? 1 : 0.5, pointerEvents: isEditable ? 'auto' : 'none' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Líneas de Pedido</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleEnviarOptimizador}
                    size="small"
                    disabled={!isEditable}
                  >
                    Enviar a Optimizador
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setModalPedidosOpen(true)}
                    size="small"
                    disabled={!isEditable}
                  >
                    + Añadir línea
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setModalRangoOpen(true)}
                    size="small"
                    disabled={!isEditable}
                  >
                    + Añadir rango de líneas
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<WarningAmberIcon />}
                    size="small"
                    disabled={!isEditable || !formData.pedidos?.some((p) => p.tiene_incidencia)}
                    onClick={() => {
                      setFormData({
                        ...formData,
                        pedidos: formData.pedidos.filter((p) => !p.tiene_incidencia),
                      })
                    }}
                  >
                    Quitar con incidencias
                  </Button>
                </Box>
              </Box>

              <CustomTable
                fields={[
                  {
                    key: 'Estado',
                    value: 'tiene_incidencia',
                    width: 70,
                    renderCell: (row) => {
                      if (!row.tiene_incidencia) return null
                      return (
                        <Tooltip
                          title={row.observaciones_incidencia || 'Pedido con incidencia'}
                          arrow
                        >
                          <WarningAmberIcon color="error" fontSize="small" />
                        </Tooltip>
                      )
                    },
                  },
                  { key: 'Cliente', value: 'cliente', width: 150 },
                  { key: 'Ejercicio', value: 'ejercicio', width: 100 },
                  {
                    key: 'Serie/Nº Pedido',
                    value: 'numero_pedido_completo',
                    width: 150,
                    renderCell: (row) => `${row.numero_serie}/${row.numero_pedido}`,
                  },
                  { key: 'Artículo', value: 'articulo', flex: 1, minWidth: 200 },
                  { key: 'Orden Planificación', value: 'numero_programacion', width: 150 },
                  {
                    key: 'Fecha Entrega',
                    value: 'fecha_entrega',
                    width: 150,
                    renderCell: (row) =>
                      row.fecha_entrega
                        ? new Date(row.fecha_entrega).toLocaleDateString('es-ES')
                        : '',
                  },
                  {
                    key: 'Acciones',
                    value: 'acciones',
                    width: 100,
                    sortable: false,
                    renderCell: (row) => (
                      <IconButton
                        size="small"
                        sx={{ alignItems: 'center' }}
                        color="error"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            pedidos: formData.pedidos.filter((p) => p.id !== row.id),
                          })
                        }}
                        disabled={!isEditable}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    ),
                  },
                ]}
                dataSource={formData.pedidos || []}
                rowid="id"
                getRowClassName={(params) =>
                  params.row.tiene_incidencia ? 'fila-con-incidencia' : ''
                }
                sx={{
                  '& .fila-con-incidencia': {
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.15)',
                    },
                  },
                }}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
              />
            </Box>
          )}
        </Box>

        {/* Botones de acción - Solo en modo EDITAR */}
        {isEditMode && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={handleCancelar}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleGuardar} disabled={!isEditable}>
              Actualizar Planificación
            </Button>
          </Box>
        )}
      </Paper>

      {/* Modal de selección de pedidos */}
      <ModalSeleccionPedidos
        open={modalPedidosOpen}
        onClose={() => setModalPedidosOpen(false)}
        pedidosSeleccionados={formData.pedidos}
        planificacionId={selectedPlanificacion?.id}
        plantaId={formData.id_planta}
        onGuardar={(nuevosPedidos) => {
          const pedidosExistentes = formData.pedidos
          const pedidosCombinados = [...pedidosExistentes]
          nuevosPedidos.forEach((nuevo) => {
            !pedidosExistentes.find((p) => p.id === nuevo.id) && pedidosCombinados.push(nuevo)
          })
          setFormData((prev) => ({ ...prev, pedidos: pedidosCombinados }))
        }}
      />

      {/* Modal de rango de pedidos */}
      <ModalRangoPedidos
        open={modalRangoOpen}
        onClose={() => setModalRangoOpen(false)}
        idPlanificacion={selectedPlanificacion?.id}
        onSuccess={(operacion, pedidos) => {
          if (operacion === 'A') {
            // Agregar pedidos: combinar con los existentes sin duplicados
            const pedidosExistentes = formData.pedidos
            const pedidosCombinados = [...pedidosExistentes]

            pedidos.forEach((nuevo) => {
              if (!pedidosExistentes.find((p) => p.id === nuevo.id)) {
                pedidosCombinados.push(nuevo)
              }
            })

            setFormData((prev) => ({ ...prev, pedidos: pedidosCombinados }))
            mostrarNotificacion(
              `${pedidos.length} pedido(s) agregados a la planificación`,
              'success',
            )
          } else if (operacion === 'B') {
            // Borrar pedidos: filtrar los que NO están en la lista a borrar
            const idsABorrar = pedidos.map((p) => p.id)
            const pedidosRestantes = formData.pedidos.filter((p) => !idsABorrar.includes(p.id))

            setFormData((prev) => ({ ...prev, pedidos: pedidosRestantes }))
            mostrarNotificacion(
              `${pedidos.length} pedido(s) eliminados de la planificación`,
              'success',
            )
          }
        }}
      />

      {/* Dialog para confirmaciones */}
      <Dialog
        open={dialog.open}
        onClose={cerrarDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{dialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{dialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={confirmarDialog} variant="contained" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
